import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AICampaignAssistant } from '@/components/campaign/ai-assistant/AICampaignAssistant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Bot, Sparkles, Zap, Activity, Brain, Network } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { DateStorageService } from '@/components/calendar/services/DateStorageService';

export const AICampaignChatPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  
  // Get campaign name from state or localStorage
  const campaignName = location.state?.campaignName || localStorage.getItem('campaignName');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-primary/5 relative">
      {/* Subtle tech pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(36,120,200,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(36,120,200,0.02)_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      
      {/* Floating orbs for tech aesthetic */}
      <div className="absolute top-20 right-20 w-32 h-32 bg-primary/5 rounded-full blur-2xl animate-pulse"></div>
      <div className="absolute bottom-32 left-16 w-24 h-24 bg-secondary/8 rounded-full blur-xl animate-pulse delay-1000"></div>

      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 border-b bg-background/95 backdrop-blur-md sticky top-0 shadow-sm"
      >
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/campaign/choice')}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {language === 'ar' ? 'العودة' : 'Back'}
                </span>
              </Button>
              
              <div className="flex items-center gap-3">
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full border border-primary/20"
                >
                  <div className="relative">
                    <Bot className="h-3 w-3" />
                    <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                  <span className="text-xs font-medium">
                    {language === 'ar' ? 'نشط' : 'Active'}
                  </span>
                </motion.div>
                
                {campaignName && (
                  <Badge variant="secondary" className="text-xs px-2 py-1">
                    {language === 'ar' ? `${campaignName}` : `${campaignName}`}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/campaign/create')}
                className="text-muted-foreground hover:text-foreground hover:bg-accent text-xs"
              >
                <Zap className="h-3 w-3 mr-1" />
                {language === 'ar' ? 'يدوي' : 'Manual'}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 py-6">
        <div className="max-w-4xl mx-auto px-4">
          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-6"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full mb-4 border border-primary/20">
              <Brain className="h-3 w-3" />
              <span className="text-xs font-medium">
                {language === 'ar' ? 'نظام الذكاء الاصطناعي' : 'AI Campaign System'}
              </span>
            </div>
            
            <motion.h1 
              className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent py-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {language === 'ar' ? 'مساعد الحملات الذكي' : 'AI Campaign Assistant'}
            </motion.h1>
            
            <motion.p 
              className="text-muted-foreground max-w-2xl mx-auto leading-relaxed text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {language === 'ar' 
                ? 'اعمل مع فريق الذكاء الاصطناعي لتصميم حملة مثالية باستخدام شبكة من 4000+ شاشة رقمية'
                : 'Work with our AI team to design the perfect campaign using our network of 4,000+ digital displays'}
            </motion.p>
          </motion.div>

          {/* AI Chat Interface */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="relative mb-6"
          >
            {/* Subtle glow effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl blur-sm"></div>
            
            <div className="relative bg-background/90 backdrop-blur-sm border border-primary/10 rounded-xl shadow-lg overflow-hidden">
              <AICampaignAssistant
                onCampaignSuggestion={(suggestion) => {
                  console.log('AICampaignChat: Campaign suggestion received:', suggestion);
                  
                  // Store the suggestion for application when navigating to create page
                  localStorage.setItem('aiSuggestionApplied', 'true');
                  localStorage.setItem('aiSuggestionData', JSON.stringify(suggestion));
                  localStorage.setItem('aiSuggestionTimestamp', new Date().toISOString());
                  
                  // Store additional campaign data
                  if (suggestion.campaignName) {
                    localStorage.setItem('campaignName', suggestion.campaignName);
                  }
                  
                  if (suggestion.duration) {
                    localStorage.setItem('duration', suggestion.duration.toString());
                  }
                  
                  if (suggestion.recommendedBudget) {
                    const budgetCalculations = {
                      totalBudget: suggestion.recommendedBudget,
                      dailyCost: Math.round(suggestion.recommendedBudget / (suggestion.duration || 14)),
                      duration: suggestion.duration,
                      campaignStrategy: suggestion.campaignStrategy,
                      targetAudience: suggestion.targetAudience
                    };
                    localStorage.setItem('budgetCalculations', JSON.stringify(budgetCalculations));
                  }
                  
                  // Set dates based on duration using DateStorageService for proper format
                  if (suggestion.duration) {
                    const today = new Date();
                    const minAllowedDate = new Date(today);
                    minAllowedDate.setDate(today.getDate() + 3); // Skip current day + 2 more days for 48-hour rule
                    minAllowedDate.setHours(0, 0, 0, 0);
                    
                    const startDate = new Date(minAllowedDate);
                    const endDate = new Date(startDate);
                    endDate.setDate(startDate.getDate() + suggestion.duration - 1);
                    
                    DateStorageService.saveDate(startDate, 'start');
                    DateStorageService.saveDate(endDate, 'end');
                    DateStorageService.saveDuration(suggestion.duration);
                  }
                  
                  navigate('/campaign/create');
                }}
                currentStep={0}
                formData={{}}
                selectedLocations={[]}
                budget={undefined}
                alwaysOpen={true}
              />
            </div>
          </motion.div>

          {/* Action Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-accent/30 backdrop-blur-sm rounded-lg border border-border/50"
          >
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span>{language === 'ar' ? 'متصل' : 'Online'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Network className="h-3 w-3 text-primary" />
                <span>{language === 'ar' ? '4000+ شاشة' : '4K+ Screens'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Activity className="h-3 w-3 text-green-500" />
                <span>{language === 'ar' ? 'تحليل فوري' : 'Live Analysis'}</span>
              </div>
            </div>
            
            <div className="flex items-center">
              <Button 
                onClick={() => navigate('/campaign/choice')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 text-base font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'ar' ? 'تغيير الطريقة' : 'Change Method'}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AICampaignChatPage;